import { getBrandSettings } from '../api/client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BrandContext = createContext(null);
const STORAGE_KEY = 'flormar-brand-settings';

const DEFAULT_SETTINGS = {
  dark: {
    primary: '#06b6d4',
    headerBg: '',
    headerGradient: '',
    bg: '#0F0B27',
    bgGradient: {
      angle: 292,
      stops: [
        { color: '#1f2a44', position: 0 },
        { color: '#4c1d95', position: 48 },
        { color: '#0ea5e9', position: 100 },
      ],
    },
    surface: '#141414',
    text: '#ffffff',
    textTitle: '',
    textBody: '',
    textMuted: '#b0b0b0',
    border: '#2a2a2a',
    highlight: '',
    secondaryBtn: '',
    navLinkColor: '',
    navLinkActive: '#ffffff',
    // `fabColor` named the floating chat button that the sidebar replaced. It is still read
    // as a fallback so brand settings saved before that change keep working.
    fabColor: '',
    sidebarAccent: '',
    logoUrl: '',
  },
  light: {
    primary: '#06b6d4',
    headerBg: '',
    headerGradient: '',
    bg: '#ffffff',
    bgGradient: {
      angle: 292,
      stops: [
        { color: '#a78bfa', position: 8 },
        { color: '#66e4fb', position: 31 },
        { color: '#ffffff', position: 46 },
      ],
    },
    surface: '#ffffff',
    text: '#000000',
    textTitle: '',
    textBody: '',
    textMuted: '#505050',
    border: '#e0e0e0',
    highlight: '',
    secondaryBtn: '',
    navLinkColor: '',
    navLinkActive: '#000000',
    // `fabColor` named the floating chat button that the sidebar replaced. It is still read
    // as a fallback so brand settings saved before that change keep working.
    fabColor: '',
    sidebarAccent: '',
    logoUrl: '',
  },
};

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function darken(hex, amount = 0.15) {
  const r = Math.max(0, Math.round(parseInt(hex.slice(1, 3), 16) * (1 - amount)));
  const g = Math.max(0, Math.round(parseInt(hex.slice(3, 5), 16) * (1 - amount)));
  const b = Math.max(0, Math.round(parseInt(hex.slice(5, 7), 16) * (1 - amount)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

const isHex = (v) => /^#[0-9a-fA-F]{6}$/.test(v);

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    return {
      dark: { ...DEFAULT_SETTINGS.dark, ...parsed.dark },
      light: { ...DEFAULT_SETTINGS.light, ...parsed.light },
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

/** Build a CSS linear-gradient string from a gradient config object. */
export function buildGradientCSS(cfg) {
  if (!cfg || !cfg.stops || cfg.stops.length < 2) return '';
  const angle = cfg.angle ?? 135;
  const stops = cfg.stops
    .filter((s) => s.color)
    .map((s, i, arr) => {
      const pos = s.position ?? Math.round((i / (arr.length - 1)) * 100);
      return `${s.color} ${pos}%`;
    })
    .join(', ');
  return `linear-gradient(${angle}deg, ${stops})`;
}

export function BrandProvider({ children }) {
  const [settings, setSettingsState] = useState(loadSettings);

  /**
   * The brand is org-wide, not per-browser. It used to live only in this admin's
   * localStorage, so every colleague saw the built-in defaults — which is exactly why one
   * user's UI looked different from another's. Fetch the shared record on mount and let it
   * win over whatever this browser happens to have cached.
   */
  useEffect(() => {
    let alive = true;
    getBrandSettings()
      .then((remote) => {
        if (!alive || !remote) return;
        setSettingsState((prev) => ({
          dark: { ...prev.dark, ...(remote.dark || {}) },
          light: { ...prev.light, ...(remote.light || {}) },
        }));
      })
      .catch(() => { /* offline or not configured — keep local/defaults */ });
    return () => { alive = false; };
  }, []);

  const applyToDOM = useCallback((s, theme) => {
    const mode = s[theme] || s.dark;
    const root = document.documentElement;

    if (mode.primary && isHex(mode.primary)) {
      root.style.setProperty('--primary', mode.primary);
      root.style.setProperty('--primary-hover', darken(mode.primary, 0.12));
      root.style.setProperty('--primary-muted', hexToRgba(mode.primary, theme === 'dark' ? 0.18 : 0.12));
    }
    if (mode.bg && isHex(mode.bg)) root.style.setProperty('--bg', mode.bg);
    if (mode.surface && isHex(mode.surface)) root.style.setProperty('--surface', mode.surface);
    if (mode.text && isHex(mode.text)) root.style.setProperty('--text', mode.text);
    if (mode.textMuted && isHex(mode.textMuted)) root.style.setProperty('--text-muted', mode.textMuted);
    if (mode.border && isHex(mode.border)) root.style.setProperty('--border', mode.border);

    if (mode.highlight && isHex(mode.highlight)) {
      root.style.setProperty('--highlight', hexToRgba(mode.highlight, theme === 'dark' ? 0.2 : 0.15));
    } else {
      root.style.removeProperty('--highlight');
    }

    if (mode.textTitle && isHex(mode.textTitle)) {
      root.style.setProperty('--text-title', mode.textTitle);
    } else {
      root.style.removeProperty('--text-title');
    }
    if (mode.textBody && isHex(mode.textBody)) {
      root.style.setProperty('--text-body', mode.textBody);
    } else {
      root.style.removeProperty('--text-body');
    }

    if (mode.secondaryBtn && isHex(mode.secondaryBtn)) {
      root.style.setProperty('--secondary-btn', mode.secondaryBtn);
      root.style.setProperty('--secondary-btn-hover', darken(mode.secondaryBtn, 0.1));
    } else {
      root.style.removeProperty('--secondary-btn');
      root.style.removeProperty('--secondary-btn-hover');
    }

    if (mode.navLinkColor && isHex(mode.navLinkColor)) {
      root.style.setProperty('--nav-link-color', mode.navLinkColor);
    } else {
      root.style.removeProperty('--nav-link-color');
    }
    if (mode.navLinkActive && isHex(mode.navLinkActive)) {
      root.style.setProperty('--nav-link-active', mode.navLinkActive);
    } else {
      root.style.removeProperty('--nav-link-active');
    }

    const accent = mode.sidebarAccent || mode.fabColor;
    if (accent && isHex(accent)) {
      root.style.setProperty('--sidebar-accent', accent);
    } else {
      root.style.removeProperty('--sidebar-accent');
    }

    const headerEl = document.querySelector('.app-header');
    if (headerEl) {
      const hGrad = typeof mode.headerGradient === 'object'
        ? buildGradientCSS(mode.headerGradient)
        : (mode.headerGradient || '');
      if (hGrad) {
        headerEl.style.background = hGrad;
      } else if (mode.headerBg && isHex(mode.headerBg)) {
        headerEl.style.background = mode.headerBg;
      } else {
        headerEl.style.background = '';
      }
    }

    const bgGrad = typeof mode.bgGradient === 'object'
      ? buildGradientCSS(mode.bgGradient)
      : (mode.bgGradient || '');
    if (bgGrad) {
      document.body.style.background = bgGrad;
      document.body.style.backgroundAttachment = 'fixed';
    } else {
      document.body.style.background = '';
      document.body.style.backgroundAttachment = '';
    }
  }, []);

  const setSettings = useCallback((updater) => {
    setSettingsState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    document.documentElement.style.cssText = '';
    document.body.style.background = '';
    document.body.style.backgroundAttachment = '';
    const headerEl = document.querySelector('.app-header');
    if (headerEl) headerEl.style.background = '';
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }, [setSettings]);

  useEffect(() => {
    const theme = document.documentElement.getAttribute('data-theme') || 'dark';
    applyToDOM(settings, theme);

    const observer = new MutationObserver(() => {
      const t = document.documentElement.getAttribute('data-theme') || 'dark';
      applyToDOM(settings, t);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [settings, applyToDOM]);

  return (
    <BrandContext.Provider value={{ settings, setSettings, resetToDefaults, DEFAULT_SETTINGS }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within BrandProvider');
  return ctx;
}
